import { Box, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import RestaurantItemImage from "../../../restaurant/RestaurantItemImage";
import { uiNeutrals } from "../../../../constants/uiNeutrals";
import {
  getValidityDisplay,
  shouldShowDealListValidity,
} from "../../../../utils/dealDisplay";
import { isDealExpired } from "../utils/calendarDealBuckets";
import {
  getSavedDeals,
  isDealSaved,
  saveDeal,
  unsaveDeal,
} from "../../../../utils/userPreferences";
import { useAuth } from "../../../../contexts/AuthContext";
import { useRequireAuth } from "../../../../hooks/useRequireAuth";

const shimmer = {
  background: `linear-gradient(90deg, ${uiNeutrals.rowDivider} 25%, #e0e0e0 50%, ${uiNeutrals.rowDivider} 75%)`,
  backgroundSize: "200% 100%",
  animation: "dealListRowShimmer 1.5s infinite",
  "@keyframes dealListRowShimmer": {
    "0%": { backgroundPosition: "-200% 0" },
    "100%": { backgroundPosition: "200% 0" },
  },
};

export const DealListRowSkeleton = ({
  compact = false,
  showSavePlaceholder = true,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: compact ? 0 : 2,
      py: 1.75,
      borderBottom: `1px solid ${uiNeutrals.rowDivider}`,
    }}
  >
    {!compact && (
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          flexShrink: 0,
          ...shimmer,
        }}
      />
    )}
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
      {!compact && (
        <Box sx={{ height: 12, width: "45%", borderRadius: 1, ...shimmer }} />
      )}
      <Box sx={{ height: 16, width: compact ? "92%" : "88%", borderRadius: 1, ...shimmer }} />
      <Box sx={{ height: 10, width: "70%", borderRadius: 1, ...shimmer }} />
    </Box>
    {showSavePlaceholder ? (
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "50%",
          ...shimmer,
        }}
      />
    ) : null}
  </Box>
);

export const DealListRow = ({
  deal,
  index,
  onExpandDeal,
  eagerImageUpToIndex = 12,
  compact = false,
  showSaveButton = true,
}) => {
  const { isAuthenticated } = useAuth();
  const requireAuth = useRequireAuth();
  const [isSaved, setIsSaved] = useState(false);

  const isExpired = isDealExpired(deal);
  const restaurantName = deal.brand?.name || "Brand";
  const shortId = deal.shortId;
  const needsSavedSync = Boolean(isAuthenticated && shortId && showSaveButton);
  const [savedSyncReady, setSavedSyncReady] = useState(!needsSavedSync);

  useEffect(() => {
    const syncNeeded = Boolean(isAuthenticated && shortId && showSaveButton);

    if (!syncNeeded) {
      setSavedSyncReady(true);
      if (shortId && isAuthenticated) {
        setIsSaved(isDealSaved(shortId));
      } else {
        setIsSaved(false);
      }
      return undefined;
    }

    setSavedSyncReady(false);
    let cancelled = false;

    const run = async () => {
      try {
        await getSavedDeals();
        if (!cancelled) {
          setIsSaved(isDealSaved(shortId));
          setSavedSyncReady(true);
        }
      } catch {
        if (!cancelled) {
          setIsSaved(false);
          setSavedSyncReady(true);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [shortId, isAuthenticated, showSaveButton]);

  useEffect(() => {
    if (!shortId || !isAuthenticated) return undefined;

    const onSavedChanged = () => {
      setIsSaved(isDealSaved(shortId));
    };

    window.addEventListener("savedDealsChanged", onSavedChanged);
    return () => window.removeEventListener("savedDealsChanged", onSavedChanged);
  }, [shortId, isAuthenticated]);

  const handleSaveClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!shortId) return;

      if (isSaved) {
        setIsSaved(false);
        void (async () => {
          const success = await unsaveDeal(shortId);
          if (!success) setIsSaved(true);
          else {
            window.dispatchEvent(
              new CustomEvent("savedDealsChanged", {
                detail: { unsavedShortId: shortId },
              }),
            );
          }
        })();
        return;
      }

      requireAuth(async () => {
        setIsSaved(true);
        const success = await saveDeal(shortId);
        if (!success) setIsSaved(false);
        else {
          window.dispatchEvent(
            new CustomEvent("savedDealsChanged", { detail: { savedDeal: deal } }),
          );
          window.dispatchEvent(new Event("loopsDealSaved"));
        }
      });
    },
    [deal, isSaved, requireAuth, shortId],
  );

  if (!savedSyncReady) {
    return (
      <DealListRowSkeleton
        compact={compact}
        showSavePlaceholder={Boolean(showSaveButton && shortId)}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 0 : 2,
        py: 1.75,
        borderBottom: `1px solid ${uiNeutrals.rowDivider}`,
        opacity: isExpired ? 0.58 : 1,
        filter: isExpired ? "grayscale(35%)" : "none",
        transition: "opacity 0.2s ease, filter 0.2s ease",
      }}
    >
      <Box
        onClick={() => onExpandDeal?.(deal)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: compact ? 0 : 2,
          flex: 1,
          minWidth: 0,
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {!compact && (
          <RestaurantItemImage
            restaurantName={restaurantName}
            imageUrl={deal.imageUrl}
            useDefaultFallback={false}
            size={56}
            borderRadius="50%"
            loading={index < eagerImageUpToIndex ? "eager" : "lazy"}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!compact && (
            <Typography
              sx={{
                color: "#666",
                fontSize: "0.75rem",
                fontWeight: 500,
                lineHeight: 1.3,
              }}
              noWrap
            >
              {restaurantName}
            </Typography>
          )}
          <Typography
            sx={{
              color: "#1a1a1a",
              fontSize: "0.9375rem",
              fontWeight: 600,
              lineHeight: 1.35,
              mt: compact ? 0 : 0.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {deal.title}
          </Typography>
          {deal.dealSummary ? (
            <Typography
              sx={{
                color: "#888",
                fontSize: "0.7rem",
                mt: 0.5,
                lineHeight: 1.3,
              }}
              noWrap
            >
              {deal.dealSummary}
            </Typography>
          ) : null}
          {shouldShowDealListValidity(deal.validity) ? (
            <Typography
              component="div"
              sx={{
                color: "#666",
                fontSize: "0.75rem",
                fontWeight: 500,
                lineHeight: 1.3,
                mt: deal.dealSummary ? 0.35 : 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minWidth: 0,
              }}
              title={getValidityDisplay(deal.validity)}
              aria-label={`Available dates: ${getValidityDisplay(deal.validity)}`}
            >
              {getValidityDisplay(deal.validity)}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {showSaveButton && shortId ? (
        <IconButton
          type="button"
          size="small"
          onClick={handleSaveClick}
          aria-label={isSaved ? "Remove from saved" : "Save deal"}
          aria-pressed={isSaved}
          sx={{
            flexShrink: 0,
            color: isSaved ? "#1a1a1a" : "#888",
            WebkitTapHighlightColor: "transparent",
            "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
          }}
        >
          {isSaved ? (
            <BookmarkIcon sx={{ fontSize: 22 }} />
          ) : (
            <BookmarkBorderIcon sx={{ fontSize: 22 }} />
          )}
        </IconButton>
      ) : null}
    </Box>
  );
};
